export type TradeAssetsTab = "receive" | "give";
export type TradeStep = "opponent" | "assets" | "review";

export const tradeStepOrder = ["opponent", "assets", "review"] as const;
export const recommendedTradeOpponentLimit = 3;

type TradeSelectionInput = {
	otherDpids: readonly unknown[];
	otherPids: readonly unknown[];
	userDpids: readonly unknown[];
	userPids: readonly unknown[];
};

export const getSelectedTradeAssetCounts = ({
	otherDpids,
	otherPids,
	userDpids,
	userPids,
}: TradeSelectionInput) => {
	const receive = otherPids.length + otherDpids.length;
	const give = userPids.length + userDpids.length;

	return {
		give,
		receive,
		total: give + receive,
	};
};

export const getInitialTradeStep = (selection: TradeSelectionInput): TradeStep =>
	getSelectedTradeAssetCounts(selection).total > 0 ? "assets" : "opponent";

export const getTradeStepState = (activeStep: TradeStep) => {
	const activeStepIndex = tradeStepOrder.indexOf(activeStep);

	return tradeStepOrder.map((step, index) => ({
		active: step === activeStep,
		complete: index < activeStepIndex,
		index,
		step,
	}));
};

export const getRecommendedTradeOpponents = <T extends { tid: number }>({
	limit = recommendedTradeOpponentLimit,
	selectedTid,
	teams,
}: {
	limit?: number;
	selectedTid: number;
	teams: readonly T[];
}) => {
	const normalizedLimit = Math.min(teams.length, Math.max(1, limit));
	const selectedTeam = teams.find((team) => team.tid === selectedTid);
	const recommendations = teams
		.filter((team) => team.tid !== selectedTid)
		.slice(0, normalizedLimit);

	if (!selectedTeam) {
		return recommendations;
	}

	return [selectedTeam, ...recommendations.slice(0, normalizedLimit - 1)];
};
